/**
 * hansker-header.js
 * ---------------------------------------------------------------------------
 * Interactions for the Hansker floating navbar (tablet + desktop).
 *
 * Markup: sections/header.liquid  ->  [data-hnsk-nav]
 * Styles: assets/hansker-custom.css
 *
 * What it does:
 *  - opens/closes the "Products" mega menu and the simple dropdowns
 *  - only one panel is open at a time
 *  - opens on hover on mouse devices, on tap/click everywhere else
 *  - closes on Escape, on click outside, when focus leaves the navbar and
 *    when the sticky header hides itself on scroll down
 *  - keeps aria-expanded in sync so screen readers announce the state
 * ---------------------------------------------------------------------------
 */

(function () {
  'use strict';

  /* Tweakables ------------------------------------------------------------ */
  var OPEN_ON_HOVER = true; // set to false to require a click on every device
  var HOVER_OPEN_DELAY = 90; // ms before a hovered menu opens
  var HOVER_CLOSE_DELAY = 220; // ms of grace before a left menu closes
  var NAVBAR_MEDIA_QUERY = '(min-width: 1200px)'; // must match the CSS breakpoint

  var FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function HanskerHeaderNav(nav) {
    this.nav = nav;
    this.openItem = null;
    this.openedBy = null; // 'hover' | 'click'
    this.hoverTimer = null;

    this.navbarQuery = window.matchMedia(NAVBAR_MEDIA_QUERY);
    this.pointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

    this.menus = Array.prototype.slice
      .call(nav.querySelectorAll('[data-hnsk-menu]'))
      .map(function (item) {
        return {
          item: item,
          trigger: item.querySelector('[data-hnsk-trigger]'),
          panel: item.querySelector('[data-hnsk-panel]'),
        };
      })
      .filter(function (menu) {
        return menu.trigger && menu.panel;
      });

    if (!this.menus.length) return;

    this.bindEvents();
  }

  HanskerHeaderNav.prototype.bindEvents = function () {
    var self = this;

    this.menus.forEach(function (menu) {
      menu.trigger.addEventListener('click', function (event) {
        event.preventDefault();
        self.toggle(menu);
      });

      menu.trigger.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        self.open(menu, 'click');
        self.focusFirstLink(menu);
      });

      if (OPEN_ON_HOVER) {
        menu.item.addEventListener('mouseenter', function () {
          if (!self.canHover()) return;
          self.clearHoverTimer();
          self.hoverTimer = setTimeout(function () {
            self.open(menu, 'hover');
          }, HOVER_OPEN_DELAY);
        });

        menu.item.addEventListener('mouseleave', function () {
          if (!self.canHover()) return;
          self.clearHoverTimer();
          self.hoverTimer = setTimeout(function () {
            if (self.openItem === menu) self.closeAll();
          }, HOVER_CLOSE_DELAY);
        });
      }
    });

    // Escape closes and puts focus back on the trigger that opened the panel
    this.nav.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !self.openItem) return;
      var trigger = self.openItem.trigger;
      self.closeAll();
      trigger.focus();
    });

    // Tabbing out of the navbar closes whatever is open
    this.nav.addEventListener('focusout', function (event) {
      if (!self.openItem) return;
      if (event.relatedTarget && self.nav.contains(event.relatedTarget)) return;
      self.closeAll();
    });

    // A click anywhere else on the page closes the panel
    this.onDocumentClick = function (event) {
      if (!self.openItem) return;
      if (self.nav.contains(event.target)) return;
      self.closeAll();
    };
    document.addEventListener('click', this.onDocumentClick);

    // Leaving the tablet/desktop breakpoint closes everything
    this.onBreakpointChange = function () {
      if (!self.navbarQuery.matches) self.closeAll();
    };
    if (this.navbarQuery.addEventListener) {
      this.navbarQuery.addEventListener('change', this.onBreakpointChange);
    } else if (this.navbarQuery.addListener) {
      this.navbarQuery.addListener(this.onBreakpointChange);
    }

    this.watchStickyHeader();
  };

  /**
   * The theme's <sticky-header> slides the whole header out of view when you
   * scroll down. An open panel would slide away with it, so close it instead.
   */
  HanskerHeaderNav.prototype.watchStickyHeader = function () {
    var self = this;
    var section = this.nav.closest('.section-header');

    if (!section || typeof MutationObserver === 'undefined') return;

    this.stickyObserver = new MutationObserver(function () {
      if (!self.openItem) return;
      if (section.classList.contains('shopify-section-header-hidden')) self.closeAll();
    });

    this.stickyObserver.observe(section, { attributes: true, attributeFilter: ['class'] });
  };

  HanskerHeaderNav.prototype.canHover = function () {
    return this.pointerQuery.matches && this.navbarQuery.matches;
  };

  HanskerHeaderNav.prototype.clearHoverTimer = function () {
    if (this.hoverTimer) clearTimeout(this.hoverTimer);
    this.hoverTimer = null;
  };

  HanskerHeaderNav.prototype.toggle = function (menu) {
    this.clearHoverTimer();

    if (this.openItem !== menu) {
      this.open(menu, 'click');
      return;
    }

    // The panel is already open because the pointer is hovering it. Clicking
    // the label then means "keep this open", not "close what I just opened".
    if (this.openedBy === 'hover') {
      this.openedBy = 'click';
      return;
    }

    this.closeAll();
  };

  HanskerHeaderNav.prototype.open = function (menu, openedBy) {
    if (this.openItem === menu) {
      this.openedBy = openedBy || this.openedBy;
      return;
    }
    if (this.openItem) this.closeAll();

    menu.item.classList.add('is-open');
    menu.trigger.setAttribute('aria-expanded', 'true');
    this.nav.classList.add('hnsk-nav--open');
    this.openItem = menu;
    this.openedBy = openedBy || 'click';
  };

  HanskerHeaderNav.prototype.closeAll = function () {
    this.clearHoverTimer();

    this.menus.forEach(function (menu) {
      menu.item.classList.remove('is-open');
      menu.trigger.setAttribute('aria-expanded', 'false');
    });

    this.nav.classList.remove('hnsk-nav--open');
    this.openItem = null;
    this.openedBy = null;
  };

  HanskerHeaderNav.prototype.focusFirstLink = function (menu) {
    var first = menu.panel.querySelector(FOCUSABLE);
    if (!first) return;

    // The panel is still visibility:hidden on the frame the class is added,
    // and a hidden element cannot take focus — wait for it to be painted.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        first.focus();
      });
    });
  };

  HanskerHeaderNav.prototype.destroy = function () {
    document.removeEventListener('click', this.onDocumentClick);
    if (this.navbarQuery.removeEventListener) {
      this.navbarQuery.removeEventListener('change', this.onBreakpointChange);
    } else if (this.navbarQuery.removeListener) {
      this.navbarQuery.removeListener(this.onBreakpointChange);
    }
    if (this.stickyObserver) this.stickyObserver.disconnect();
  };

  /**
   * Mobile + tablet menu drawer (below 990px).
   * Opened by the hamburger in the bar, closed by the X, Escape, or a resize
   * up to desktop. Focus is trapped inside while it is open and the page
   * behind it cannot scroll.
   */
  function HanskerMenuDrawer(drawer) {
    this.drawer = drawer;
    this.panel = drawer.querySelector('.hnsk-drawer__panel') || drawer;
    this.burger = document.querySelector('[data-hnsk-burger]');
    this.closeButton = drawer.querySelector('[data-hnsk-drawer-close]');
    this.desktopQuery = window.matchMedia(NAVBAR_MEDIA_QUERY);
    this.isOpen = false;

    if (!this.burger) return;

    this.bindEvents();
  }

  HanskerMenuDrawer.prototype.bindEvents = function () {
    var self = this;

    this.burger.addEventListener('click', function (event) {
      event.preventDefault();
      self.open();
    });

    if (this.closeButton) {
      this.closeButton.addEventListener('click', function (event) {
        event.preventDefault();
        self.close();
      });
    }

    // Escape closes; Tab is kept inside the drawer while it is open
    this.onKeydown = function (event) {
      if (!self.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        self.close();
        return;
      }
      if (event.key === 'Tab') self.trapFocus(event);
    };
    document.addEventListener('keydown', this.onKeydown);

    // Following a link closes the drawer, so going "back" does not reveal it open
    this.drawer.addEventListener('click', function (event) {
      var link = event.target.closest ? event.target.closest('a[href]') : null;
      if (link) self.close({ restoreFocus: false });
    });

    // Growing past the desktop breakpoint hands over to the inline navbar.
    // Watched two ways on purpose: leaving the drawer open past the breakpoint
    // would strand the scroll lock and make the page unscrollable, so a missed
    // matchMedia event must not be the only thing standing between us and that.
    this.onBreakpointChange = function () {
      if (self.desktopQuery.matches && self.isOpen) self.close({ restoreFocus: false });
    };
    if (this.desktopQuery.addEventListener) {
      this.desktopQuery.addEventListener('change', this.onBreakpointChange);
    } else if (this.desktopQuery.addListener) {
      this.desktopQuery.addListener(this.onBreakpointChange);
    }

    this.onResize = function () {
      if (self.resizeFrame) return;
      self.resizeFrame = requestAnimationFrame(function () {
        self.resizeFrame = null;
        self.onBreakpointChange();
      });
    };
    window.addEventListener('resize', this.onResize);
  };

  HanskerMenuDrawer.prototype.open = function () {
    if (this.isOpen) return;

    this.isOpen = true;
    this.drawer.classList.add('is-open');
    this.burger.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('hnsk-menu-open');

    var first = this.closeButton || this.drawer.querySelector(FOCUSABLE);
    if (first) {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          first.focus();
        });
      });
    }
  };

  HanskerMenuDrawer.prototype.close = function (options) {
    if (!this.isOpen) return;
    var restoreFocus = !options || options.restoreFocus !== false;

    this.isOpen = false;
    this.drawer.classList.remove('is-open');
    this.burger.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('hnsk-menu-open');

    if (restoreFocus) this.burger.focus();
  };

  HanskerMenuDrawer.prototype.trapFocus = function (event) {
    var focusable = Array.prototype.slice
      .call(this.drawer.querySelectorAll(FOCUSABLE))
      .filter(function (el) {
        return el.offsetParent !== null;
      });

    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  HanskerMenuDrawer.prototype.destroy = function () {
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
    if (this.desktopQuery && this.desktopQuery.removeEventListener) {
      this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
    } else if (this.desktopQuery && this.desktopQuery.removeListener) {
      this.desktopQuery.removeListener(this.onBreakpointChange);
    }
    document.documentElement.classList.remove('hnsk-menu-open');
  };

  /* Boot ------------------------------------------------------------------ */
  var instances = [];

  function init(root) {
    destroy();
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.slice.call(scope.querySelectorAll('[data-hnsk-nav]')).forEach(function (nav) {
      instances.push(new HanskerHeaderNav(nav));
    });
    Array.prototype.slice.call(scope.querySelectorAll('[data-hnsk-drawer]')).forEach(function (drawer) {
      instances.push(new HanskerMenuDrawer(drawer));
    });
  }

  function destroy() {
    instances.forEach(function (instance) {
      if (typeof instance.destroy === 'function') instance.destroy();
    });
    instances = [];
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Re-bind when the header is re-rendered in the theme editor
  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target || !event.target.querySelector) return;
    if (event.target.querySelector('[data-hnsk-nav], [data-hnsk-drawer]')) init(document);
  });
})();
